(function(){
  'use strict';
  const ui=()=>window.SM.UI, util=()=>window.SM.UTIL;

  // ─── HIDDEN PLATFORM IDs ──────────────────────────────────────
  // System platform rows have company_id = NULL and are protected by
  // Supabase RLS — we cannot UPDATE them directly from the browser.
  // Instead we store an array of hidden platform IDs on the company
  // record (companies.hidden_platform_ids jsonb[]).  Custom platforms
  // (company_id = our company) can still be toggled via is_active.

  async function getHiddenIds(){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data}=await client
      .from('companies')
      .select('hidden_platform_ids')
      .eq('id',window.SM.AUTH.company?.id)
      .maybeSingle();
    return data?.hidden_platform_ids||[];
  }

  async function setHiddenIds(ids){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    await client
      .from('companies')
      .update({hidden_platform_ids:ids})
      .eq('id',window.SM.AUTH.company?.id);
    // Keep local company object in sync so other modules see it
    if(window.SM.AUTH.company) window.SM.AUTH.company.hidden_platform_ids=ids;
  }

  async function load(){
    const content=document.getElementById('ims-platforms-content');
    if(!content)return;
    content.innerHTML=`<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>`;

    const companyId=window.SM.AUTH.company?.id;
    if(!companyId){
      content.innerHTML=`<div class="ims__coming-soon"><i class="ti ti-apps"></i><h3>Not ready</h3><p>Company not loaded. Please refresh.</p></div>`;
      return;
    }

    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);

    try{
      const [{data:sys},{data:custom},{data:terms},hiddenIds]=await Promise.all([
        client.from('platforms').select('id,name,order_type,categories,is_active,is_system,sort_order').is('company_id',null),
        client.from('platforms').select('id,name,order_type,categories,is_active,is_system,sort_order').eq('company_id',companyId),
        client.from('platform_terms').select('*'),
        getHiddenIds()
      ]);

      const hiddenSet=new Set(hiddenIds||[]);

      // Merge: for system platforms, is_active is overridden by hiddenSet
      const all=[...(sys||[]).map(p=>({...p,is_active:!hiddenSet.has(p.id)})),...(custom||[])]
        .sort((a,b)=>(a.sort_order||999)-(b.sort_order||999)||(a.name||'').localeCompare(b.name||''));

      // Attach terms
      const termsMap={};
      (terms||[]).forEach(t=>{termsMap[t.platform_id]=t;});
      all.forEach(p=>{p._terms=termsMap[p.id]||null;});

      if(!all.length){
        content.innerHTML=`<div class="ims__coming-soon"><i class="ti ti-apps"></i><h3>No platforms found</h3></div>`;
        return;
      }

      const systemPlatforms=all.filter(p=>p.is_system);
      const customPlatforms=all.filter(p=>!p.is_system);
      const visiblePlatforms=all.filter(p=>p.is_active);
      const hiddenPlatforms=all.filter(p=>!p.is_active);

      content.innerHTML=`
        <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
          <i class="ti ti-info-circle"></i>
          <span>
            <strong>Hiding a platform</strong> removes it from the order entry dropdown — existing orders are not affected.
            Use the <i class="ti ti-settings" style="font-size:12px"></i> <strong>Terms</strong> button to set the default delivery method, pricing basis and VAT treatment per platform.
          </span>
        </div>
        <div class="ims__tabs" id="ims-plat-tabs">
          <div class="ims__tab ims__tab--active" data-tab="all">All (${all.length})</div>
          <div class="ims__tab" data-tab="visible">Visible (${visiblePlatforms.length})</div>
          <div class="ims__tab" data-tab="hidden" style="${hiddenPlatforms.length>0?'color:var(--ims-warning);font-weight:600':''}">Hidden (${hiddenPlatforms.length})</div>
          <div class="ims__tab" data-tab="system">System (${systemPlatforms.length})</div>
          <div class="ims__tab" data-tab="custom">Custom (${customPlatforms.length})</div>
        </div>
        <div id="ims-plat-content">${renderPlatforms(all)}</div>`;

      document.querySelectorAll('#ims-plat-tabs .ims__tab').forEach(tab=>{
        tab.addEventListener('click',()=>{
          document.querySelectorAll('#ims-plat-tabs .ims__tab').forEach(t=>t.classList.remove('ims__tab--active'));
          tab.classList.add('ims__tab--active');
          const filtered=
            tab.dataset.tab==='visible' ? visiblePlatforms :
            tab.dataset.tab==='hidden'  ? hiddenPlatforms  :
            tab.dataset.tab==='system'  ? systemPlatforms  :
            tab.dataset.tab==='custom'  ? customPlatforms  : all;
          const c=document.getElementById('ims-plat-content');
          if(c)c.innerHTML=renderPlatforms(filtered);
        });
      });

    }catch(err){
      content.innerHTML=`<div class="ims__coming-soon"><i class="ti ti-alert-circle"></i><h3>Error loading platforms</h3><p>${util().sanitize(err.message||String(err))}</p></div>`;
    }
  }

  function renderPlatforms(platforms){
    if(!platforms.length)return`<div class="ims__empty"><i class="ti ti-apps"></i><p>No platforms in this view</p></div>`;
    return`<div class="ims__table-wrap"><table class="ims__table">
      <thead><tr><th>Platform</th><th>Type</th><th>Pricing</th><th>Delivery</th><th>VAT</th><th>Visibility</th><th>Source</th><th></th></tr></thead>
      <tbody>${platforms.map(p=>{
        const t=p._terms;
        const isHidden=!p.is_active;
        return`<tr style="${isHidden?'opacity:0.5':''}">
          <td>
            <div style="font-weight:600;${isHidden?'color:var(--ims-text-muted)':''}">${util().sanitize(p.name)}</div>
            ${isHidden?'<div style="font-size:10px;color:var(--ims-warning);font-weight:500"><i class="ti ti-eye-off" style="font-size:10px"></i> Hidden from orders</div>':''}
          </td>
          <td><span class="ims__pill ims__pill--${p.order_type==='wholesale'?'info':'neutral'}">${p.order_type==='wholesale'?'Wholesale':'Retail'}</span></td>
          <td style="font-size:12px">${t?fmtPricing(t.pricing_basis):'Default'}</td>
          <td style="font-size:12px">${t?fmtDelivery(t.delivery_arrangement):'We deliver'}</td>
          <td style="font-size:12px">${t?fmtVAT(t.vat_treatment):'Standard'}</td>
          <td>
            <span class="ims__pill ims__pill--${p.is_active?'success':'warning'}">
              <i class="ti ti-${p.is_active?'eye':'eye-off'}" style="font-size:10px"></i>
              &nbsp;${p.is_active?'Visible':'Hidden'}
            </span>
          </td>
          <td><span class="ims__pill ims__pill--${p.is_system?'accent':'neutral'}">${p.is_system?'System':'Custom'}</span></td>
          <td>
            <div class="ims__btn-group">
              <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon"
                onclick="window.IMS_PLATFORMS.editTerms('${p.id}','${util().sanitize(p.name)}')"
                title="Set delivery method &amp; terms">
                <i class="ti ti-settings"></i>
              </button>
              ${!p.is_system?`
                <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon"
                  onclick="window.IMS_PLATFORMS.editPlatform('${p.id}')"
                  title="Edit platform name/type">
                  <i class="ti ti-edit"></i>
                </button>`:''
              }
              <button
                class="ims__btn ims__btn--${p.is_active?'danger':'success'} ims__btn--sm"
                onclick="window.IMS_PLATFORMS.toggleActive('${p.id}',${!p.is_active},${!!p.is_system},'${util().sanitize(p.name)}')"
                title="${p.is_active?'Hide from order entry':'Show in order entry'}">
                <i class="ti ti-${p.is_active?'eye-off':'eye'}"></i>
                ${p.is_active?'Hide':'Show'}
              </button>
            </div>
          </td>
        </tr>`;
      }).join('')}
      </tbody>
    </table></div>`;
  }

  function fmtPricing(b){
    return{
      wholesale_excl_vat:'Wholesale excl. VAT',wholesale_incl_vat:'Wholesale incl. VAT',
      rrp_excl_vat:'RRP excl. VAT',rrp_incl_vat:'RRP incl. VAT',
      custom_per_product:'Custom / product'
    }[b]||b||'Default';
  }

  function fmtDelivery(a){
    return{
      we_deliver:'We deliver',customer_collects:'Customer collects',
      walk_in:'Walk-in',flexible:'Flexible',third_party:'3rd party courier'
    }[a]||a||'We deliver';
  }

  function fmtVAT(v){
    return{standard:'Standard 20%',zero_rated:'Zero rated',exempt:'Exempt',reverse_charge:'Reverse charge'}[v]||v||'Standard';
  }

  function showNewPlatform(){
    ui().openModal('Add custom platform',`
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Platform name</label>
          <input class="ims__input" type="text" id="ims-np-name" placeholder="e.g. My Website, Trade Show, Walk-in">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Order type</label>
          <select class="ims__select" id="ims-np-type">
            <option value="retail">Retail</option>
            <option value="wholesale">Wholesale</option>
          </select>
        </div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_PLATFORMS.savePlatform('')"><i class="ti ti-check"></i> Add</button>
    `,'sm');
  }

  async function editPlatform(id){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:p}=await client.from('platforms').select('*').eq('id',id).maybeSingle();
    if(!p)return;
    ui().openModal(`Edit: ${p.name}`,`
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Name</label>
          <input class="ims__input" type="text" id="ims-np-name" value="${util().sanitize(p.name||'')}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Order type</label>
          <select class="ims__select" id="ims-np-type">
            <option value="retail" ${p.order_type==='retail'?'selected':''}>Retail</option>
            <option value="wholesale" ${p.order_type==='wholesale'?'selected':''}>Wholesale</option>
          </select>
        </div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_PLATFORMS.savePlatform('${id}')"><i class="ti ti-check"></i> Save</button>
    `,'sm');
  }

  async function savePlatform(existingId){
    const name=document.getElementById('ims-np-name')?.value?.trim();
    const type=document.getElementById('ims-np-type')?.value||'retail';
    if(!name){ui().toast('Name required','error');return;}
    const companyId=window.SM.AUTH.company?.id;
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {error}=existingId
      ?await client.from('platforms').update({name,order_type:type}).eq('id',existingId)
      :await client.from('platforms').insert({name,order_type:type,categories:['all'],company_id:companyId,is_system:false,is_active:true,sort_order:999});
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().closeModal();ui().toast(existingId?'Updated':'Added','success');load();
  }

  // ─── TOGGLE ACTIVE ────────────────────────────────────────────
  // For SYSTEM platforms (company_id IS NULL): RLS blocks direct UPDATE,
  // so we store hidden IDs in companies.hidden_platform_ids instead.
  // For CUSTOM platforms (company_id = ours): update is_active directly.
  async function toggleActive(id, newActive, isSystem, name){
    const action=newActive?'show':'hide';

    if(isSystem){
      // Read current hidden list, add/remove this ID, write back
      const hidden=await getHiddenIds();
      const hiddenSet=new Set(hidden);
      if(newActive){
        hiddenSet.delete(id);
      } else {
        hiddenSet.add(id);
      }
      const {error}=await (async()=>{
        const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
        return client.from('companies')
          .update({hidden_platform_ids:[...hiddenSet]})
          .eq('id',window.SM.AUTH.company?.id);
      })();
      if(error){ui().toast('Failed to update: '+error.message,'error');return;}
      if(window.SM.AUTH.company) window.SM.AUTH.company.hidden_platform_ids=[...hiddenSet];
    } else {
      // Custom platform — update is_active directly (RLS allows this)
      const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
      const {error}=await client.from('platforms').update({is_active:newActive}).eq('id',id);
      if(error){ui().toast('Failed to update: '+error.message,'error');return;}
    }

    ui().toast(
      `${util().sanitize(name)} ${newActive?'is now visible in order entry':'is now hidden from order entry'}`,
      newActive?'success':'info'
    );
    load();
  }

  // ─── TERMS MODAL ──────────────────────────────────────────────
  async function editTerms(platformId, platformName){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:existing}=await client.from('platform_terms').select('*').eq('platform_id',platformId).maybeSingle();
    const t=existing||{};
    const currentDelivery=t.delivery_arrangement||'we_deliver';

    const sel=(id,opts,field)=>`<select class="ims__select" id="${id}">${
      opts.map(o=>`<option value="${o.v}" ${t[field]===o.v?'selected':''}>${o.l}</option>`).join('')
    }</select>`;

    const deliveryOpts=[
      {v:'we_deliver',        icon:'ti-truck-delivery', label:'We deliver',         sub:'MDF delivers to customer address'},
      {v:'customer_collects', icon:'ti-building-store', label:'Customer collects',  sub:'Customer picks up from our warehouse'},
      {v:'walk_in',           icon:'ti-walk',           label:'Walk-in',            sub:'Customer walks in and takes goods on the day'},
      {v:'flexible',          icon:'ti-arrows-exchange',label:'Flexible',           sub:'Decided per order — no fixed default'},
      {v:'third_party',       icon:'ti-package',        label:'3rd party courier',  sub:'Customer arranges their own courier'},
    ];

    ui().openModal(`Terms &amp; delivery: ${platformName}`,`
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>These are <strong>defaults only</strong> — you can override the delivery method on individual orders at any time.</span>
      </div>

      <div style="margin-bottom:16px">
        <div style="font-size:13px;font-weight:600;color:var(--ims-text);margin-bottom:8px">
          <i class="ti ti-truck" style="color:var(--ims-accent)"></i> &nbsp;Default delivery method
        </div>
        <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px" id="ims-pt-delivery-cards">
          ${deliveryOpts.map(opt=>deliveryCard(opt,currentDelivery)).join('')}
        </div>
      </div>

      <div class="ims__form-grid">
        <div class="ims__form-group">
          <label class="ims__label">Delivery charges paid by</label>
          ${sel('ims-pt-del-charges',[
            {v:'our_account',       l:'Our account (we pay)'},
            {v:'their_account',     l:'Their account (they pay)'},
            {v:'included_in_price', l:'Included in price'},
            {v:'charged_separately',l:'Charged separately'},
            {v:'not_applicable',    l:'N/A — no delivery'},
          ],'delivery_charges')}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Pricing basis</label>
          ${sel('ims-pt-pricing',[
            {v:'rrp_incl_vat',       l:'RRP incl. VAT (retail)'},
            {v:'rrp_excl_vat',       l:'RRP excl. VAT'},
            {v:'wholesale_incl_vat', l:'Wholesale incl. VAT'},
            {v:'wholesale_excl_vat', l:'Wholesale excl. VAT'},
            {v:'custom_per_product', l:'Custom per product'},
          ],'pricing_basis')}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">VAT treatment</label>
          ${sel('ims-pt-vat',[
            {v:'standard',      l:'Standard 20%'},
            {v:'zero_rated',    l:'Zero rated'},
            {v:'exempt',        l:'Exempt'},
            {v:'reverse_charge',l:'Reverse charge'},
          ],'vat_treatment')}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Invoice format</label>
          ${sel('ims-pt-invoice',[
            {v:'standard_vat',l:'Standard VAT invoice'},
            {v:'proforma',    l:'Pro forma'},
            {v:'commercial',  l:'Commercial (exports)'},
            {v:'no_vat',      l:'No VAT shown'},
            {v:'receipt',     l:'Receipt (walk-in / cash)'},
          ],'invoice_format')}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Payment terms</label>
          <select class="ims__select" id="ims-pt-payment">
            ${['Immediate / cash','Advance payment','7 days','14 days','30 days','60 days','90 days'].map(o=>`
              <option ${t.payment_terms===o?'selected':''}>${o}</option>`).join('')}
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Price label on invoice</label>
          <input class="ims__input" type="text" id="ims-pt-label"
            value="${util().sanitize(t.price_label||'Price')}" placeholder="e.g. Unit price">
        </div>
      </div>

      <div class="ims__alert ims__alert--warning" style="margin-top:12px">
        <i class="ti ti-bulb"></i>
        <span>
          <strong>Quick guide:</strong> &nbsp;
          Amazon / eBay → We deliver, Our account &nbsp;|&nbsp;
          Wholesale (Faire etc.) → Flexible, Their account &nbsp;|&nbsp;
          Walk-in customer → Walk-in, N/A &nbsp;|&nbsp;
          Wayfair / Temu → Customer collects, Their account
        </span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_PLATFORMS.saveTerms('${platformId}','${existing?.id||''}')">
        <i class="ti ti-check"></i> Save terms
      </button>
    `,'lg');
  }

  function deliveryCard(opt, selected){
    const active=selected===opt.v;
    return`<label style="display:flex;flex-direction:column;gap:5px;padding:10px 8px;
      border:2px solid ${active?'var(--ims-accent)':'var(--ims-border)'};
      border-radius:var(--ims-radius);cursor:pointer;
      background:${active?'var(--ims-accent-bg)':'var(--ims-surface2)'};
      transition:all .15s;text-align:center;align-items:center"
      onclick="window.IMS_PLATFORMS.selectDelivery('${opt.v}')">
      <input type="radio" name="ims-delivery-opt" value="${opt.v}" ${active?'checked':''} style="display:none">
      <i class="ti ${opt.icon}" style="font-size:20px;color:${active?'var(--ims-accent)':'var(--ims-text-muted)'}"></i>
      <div style="font-size:11px;font-weight:600;color:${active?'var(--ims-accent)':'var(--ims-text)'};line-height:1.2">${opt.label}</div>
      <div style="font-size:10px;color:var(--ims-text-muted);line-height:1.2">${opt.sub}</div>
    </label>`;
  }

  function selectDelivery(value){
    const opts=[
      {v:'we_deliver',        icon:'ti-truck-delivery', label:'We deliver',         sub:'MDF delivers to customer address'},
      {v:'customer_collects', icon:'ti-building-store', label:'Customer collects',  sub:'Customer picks up from our warehouse'},
      {v:'walk_in',           icon:'ti-walk',           label:'Walk-in',            sub:'Customer walks in and takes goods on the day'},
      {v:'flexible',          icon:'ti-arrows-exchange',label:'Flexible',           sub:'Decided per order — no fixed default'},
      {v:'third_party',       icon:'ti-package',        label:'3rd party courier',  sub:'Customer arranges their own courier'},
    ];
    const container=document.getElementById('ims-pt-delivery-cards');
    if(!container)return;
    container.innerHTML=opts.map(opt=>deliveryCard(opt,value)).join('');
  }

  async function saveTerms(platformId, existingTermsId){
    const g=id=>document.getElementById(id)?.value||'';
    const deliveryRadio=document.querySelector('input[name="ims-delivery-opt"]:checked');
    const data={
      platform_id:          platformId,
      pricing_basis:        g('ims-pt-pricing'),
      price_label:          g('ims-pt-label'),
      delivery_arrangement: deliveryRadio?.value||'we_deliver',
      delivery_charges:     g('ims-pt-del-charges'),
      vat_treatment:        g('ims-pt-vat'),
      invoice_format:       g('ims-pt-invoice'),
      payment_terms:        g('ims-pt-payment')
    };
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {error}=existingTermsId
      ?await client.from('platform_terms').update(data).eq('id',existingTermsId)
      :await client.from('platform_terms').insert(data);
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().closeModal();ui().toast('Terms saved','success');load();
  }

  window.addEventListener('ims:panel',e=>{if(e.detail.panel==='platforms')load();});
  window.addEventListener('ims:ready',()=>{
    document.getElementById('ims-platform-new-btn')?.addEventListener('click',showNewPlatform);
  });

  window.IMS_PLATFORMS={load,showNewPlatform,editPlatform,savePlatform,editTerms,selectDelivery,saveTerms,toggleActive};
})();
